import React, { useEffect, useRef } from 'react';
import { useLocation } from '@reach/router';

import { useCurrentUser } from '~/src/common/user';
import { voiceflow } from '~/src/common/voiceflow';

import { useFeatureFlag } from './hooks';

const {
  PROJECT_ID_LOGGED_IN,
  // PROJECT_ID_LOGGED_OUT
} = voiceflow;

/***
 * Hide/show Voiceflow UI intention:
 *   - if pathname matches `allowOnPaths`, ensure Voiceflow UI shown
 *   - for any other pathname, hide it
 */
const allowOnPaths = [
  new RegExp('/ecourse/trauma-awareness-a-yogic-framework'),
  new RegExp('/ecourse/player/trauma-awareness-a-yogic-framework'),
  new RegExp('/ecourse/certificate/trauma-awareness-a-yogic-framework'),
];

// Singleton component, global state not designed for multiple instances
export const VoiceflowSingleton: React.FC = () => {
  const location = useLocation();
  const { isLoggedIn, loading: loadingUser } = useCurrentUser();

  const featureFlag = useFeatureFlag('ai_chat');

  const loading = loadingUser || featureFlag.loading;
  const flagEnabled = featureFlag.enabled;

  const projectIdRef = useRef<null | string>(null);

  useEffect(() => {
    if (loading || !flagEnabled) return;

    (async () => {
      const { pathname } = location;

      if (allowOnPaths.some(re => re.test(pathname))) {
        // NOTE: currently not designed to reload project if this changes at runtime.
        // Conceptually should be able to use .destroy() and then .load() again, but
        // Voiceflow doesn't seem to be designed with this intent, and bugs encountered.

        if (!projectIdRef.current) {
          // This component was designed to serve different project IDs to logged in and logged out,
          // but until we fix issues with transitions, we are disabling logged out
          // projectIdRef.current = isLoggedIn ? PROJECT_ID_LOGGED_IN : PROJECT_ID_LOGGED_OUT;
          projectIdRef.current = PROJECT_ID_LOGGED_IN;
        }
        await voiceflow.ensureLoaded(projectIdRef.current);
        voiceflow.show();
      } else {
        const loaded = await voiceflow.wasLoaded();
        if (loaded) voiceflow.hide();
      }
    })();
  }, [location, loading, flagEnabled, isLoggedIn]);

  return null;
};
